import { NEWS_LIST_PATH, buildNewsListBrowsePath } from './newsListRoutes';

/**
 * ### NEWS_CATEGORY_ALL_ID
 *
 * Identifier of the unfiltered option. It is never sent to the list API:
 * omitting `category` is what returns every article, including the ones no
 * editor has categorised yet.
 */
export const NEWS_CATEGORY_ALL_ID = 'all';

/**
 * ### NEWS_CATEGORY_FILTER_IDS
 *
 * Board category ids the newsroom offers as filters, in display order.
 * Each value is a `bcat_id` the list API resolves against its category table;
 * an id nobody has registered on the board resolves to nothing and the API then
 * answers with the unfiltered list, so a filtered response is only trustworthy
 * once `isNewsCategoryAppliedByServer` confirms it.
 */
export const NEWS_CATEGORY_FILTER_IDS = ['industrial', 'safety'] as const;

/**
 * ### NewsCategoryFilterId
 *
 * Board category id of a newsroom filter, excluding the unfiltered option.
 */
export type NewsCategoryFilterId = (typeof NEWS_CATEGORY_FILTER_IDS)[number];

/**
 * ### NewsCategoryOptionId
 *
 * Identifier of a rendered filter control, the unfiltered option included.
 */
export type NewsCategoryOptionId =
  NewsCategoryFilterId | typeof NEWS_CATEGORY_ALL_ID;

/**
 * ### NEWS_CATEGORY_FILTER_LABEL_KEYS
 *
 * Translation key of each filter control. The keys sit in this module rather
 * than in the component so the App Router translation manifest, which walks
 * runtime imports looking for key literals, collects them for the news list.
 */
export const NEWS_CATEGORY_FILTER_LABEL_KEYS: Record<
  NewsCategoryOptionId,
  string
> = {
  all: '전체',
  industrial: 'Industrial',
  safety: 'Safety',
};

/**
 * ### NewsCategoryFilterOption
 *
 * One rendered filter control: where it navigates and whether it is the
 * selection the current list reflects.
 */
export type NewsCategoryFilterOption = {
  href: string;
  id: NewsCategoryOptionId;
  isSelected: boolean;
};

function readCategoryId(category: unknown): string | undefined {
  if (typeof category !== 'object' || category === null) return undefined;

  const { bcat_id: categoryId } = category as { bcat_id?: unknown };
  return typeof categoryId === 'string' ? categoryId : undefined;
}

function toCategoryArray(categories: unknown): ReadonlyArray<unknown> {
  return Array.isArray(categories) ? categories : [];
}

/**
 * ### readRegisteredNewsCategoryIds
 *
 * Keeps the newsroom filter ids an administrator has actually registered on the
 * board, in display order. Categories the board carries for other purposes are
 * dropped, and an unrecognised shape counts as no category at all.
 *
 * @param categories Category records the board API returned, in any shape
 * @returns The newsroom filter ids the board can filter by today
 */
export function readRegisteredNewsCategoryIds(
  categories: unknown,
): Array<NewsCategoryFilterId> {
  const registered = new Set(toCategoryArray(categories).map(readCategoryId));

  return NEWS_CATEGORY_FILTER_IDS.filter((id) => registered.has(id));
}

/**
 * ### isNewsCategoryAppliedByServer
 *
 * Tells whether the list response was actually narrowed to the requested
 * category. The API echoes the categories it resolved, and an id it cannot
 * resolve leaves that echo empty while the results stay unfiltered — which
 * would otherwise show every article under a category tab.
 *
 * @param params.appliedCategories Categories the list response echoed back
 * @param params.categoryId Category the request asked for, if any
 * @returns `true` when the results match the request, including when nothing was requested
 */
export function isNewsCategoryAppliedByServer({
  appliedCategories,
  categoryId,
}: {
  appliedCategories: unknown;
  categoryId: string | undefined;
}): boolean {
  if (!categoryId) return true;

  return toCategoryArray(appliedCategories).some(
    (category) => readCategoryId(category) === categoryId,
  );
}

/**
 * ### buildNewsCategoryFilterOptions
 *
 * Builds the filter controls for the news list. Nothing is rendered while the
 * board has no newsroom category registered and none is selected, so the
 * newsroom never shows a control that cannot narrow anything. A selected id
 * always yields at least the unfiltered control, which keeps a shared or
 * hand-typed category URL from dead-ending on an empty list.
 *
 * @param params.registeredCategoryIds Filter ids the board can filter by
 * @param params.selectedCategoryId Category the current list was requested with, if any
 * @returns The controls to render, unfiltered option first
 */
export function buildNewsCategoryFilterOptions({
  registeredCategoryIds,
  selectedCategoryId,
}: {
  registeredCategoryIds: ReadonlyArray<NewsCategoryFilterId>;
  selectedCategoryId: string | undefined;
}): Array<NewsCategoryFilterOption> {
  const visibleIds = NEWS_CATEGORY_FILTER_IDS.filter(
    (id) => registeredCategoryIds.includes(id) || id === selectedCategoryId,
  );

  if (visibleIds.length === 0 && !selectedCategoryId) return [];

  return [
    {
      href: NEWS_LIST_PATH,
      id: NEWS_CATEGORY_ALL_ID,
      isSelected: !selectedCategoryId,
    },
    ...visibleIds.map((id) => ({
      href: buildNewsListBrowsePath({ category: id }),
      id,
      isSelected: id === selectedCategoryId,
    })),
  ];
}
